/**
 * Shared pure helpers for the Cash Reserves dashboard: formatting, the
 * management-fee rules, family grouping, and column sorting.
 */

// ─── formatting helpers ──────────────────────────────────────────────────────
export const fmtCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const fmtCurrencyPrecise = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const DEFAULT_DAYS = 30;

// Truncate a long name at a word boundary, adding an ellipsis. Used for the
// family label so it stays scannable (e.g. "401 K Plan American United...").
export const MAX_FAMILY_NAME_LEN = 30;
export const truncateName = (name, max = MAX_FAMILY_NAME_LEN) => {
  if (!name || name.length <= max) return name;
  const cut = name.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  const base = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd();
  return `${base}...`;
};

const MANAGEMENT_FEE_CATEGORY = 'management fee';

// Exact equality, deliberately — never relax this to startsWith/includes. The
// quarterly fee snapshots the ingest mints are categorized "Management Fee —
// Q1 2026"; they must NOT match. They can't reach the dashboard payload today
// (both history loaders exclude them structurally), but a prefix match would
// mean that if one ever did, it would silently inflate the management-fee side
// of the Configured Reserves footer and count as satisfying the fee
// requirement — with the real reserve's dollars double counted alongside it.
export const isManagementFee = (reserve) =>
  (reserve.reserve_category || '').trim().toLowerCase() === MANAGEMENT_FEE_CATEGORY;

// True when an account's only reserve(s) are the Management Fee category, so a
// reviewer can choose to overlook accounts that carry nothing but that reserve.
export const isManagementFeeOnly = (account) => {
  const reserves = account.reserves || [];
  return reserves.length > 0 && reserves.every(isManagementFee);
};

// Total dollar value of an account's Management Fee reserves.
export const managementFeeDollars = (account) =>
  (account.reserves || [])
    .filter(isManagementFee)
    .reduce((sum, r) => sum + (r.calculated_reserve_dollar || 0), 0);

// True when an account carries at least one Management Fee cash reserve.
export const hasManagementFeeReserve = (account) =>
  (account.reserves || []).some(isManagementFee);

// True when this account should still trigger the management-fee warning.
export const accountMissingManagementFee = (account) =>
  !hasManagementFeeReserve(account) && !account.ignored;

// A reserve is recurring only when explicitly marked so; anything else (No,
// blank, ...) is treated as a one-time / non-recurring reserve.
const RECURRING_TRUE = new Set(['yes', 'true', '1', 'y', 'recurring']);
export const isRecurringReserve = (reserve) =>
  RECURRING_TRUE.has(String(reserve.recurring ?? '').trim().toLowerCase());
export const hasNonRecurringReserve = (account) =>
  (account.reserves || []).some((r) => !isRecurringReserve(r));

export const REASON_LABELS = {
  time_elapsed: 'Time elapsed (>90 days)',
  new_outflow: 'New outflow since review',
  reserve_changed: 'Reserve added/removed',
};

// Halter Ferguson Financial is the firm itself, not a client to review, so it is
// always omitted from the dashboard. Matched by its known account number and by
// name (account or rebalancing group) to be robust to naming variations.
const HFF_ACCOUNT_NUMBERS = new Set(['08003436']);
const HFF_NAME_PATTERN = /halter\s*ferguson/i;
export const isFirmAccount = (account) =>
  HFF_ACCOUNT_NUMBERS.has(String(account.account_number || '')) ||
  HFF_NAME_PATTERN.test(account.account_name || '') ||
  HFF_NAME_PATTERN.test(account.family_name || '');

// An account is "actionable" (Needs Attention) only when it has a concrete
// suggested action — an alert. A stale-but-alert-free review is its own,
// separate concern (see showsNeedsRereview) and must not inflate the Needs
// Attention count/filter with accounts that have nothing to act on. A
// reviewed account is never actionable — "Reviewed" replaces the flag.
export const showsNeedsAttention = (account) =>
  account.review_status !== 'reviewed' && account.alert_level != null;

// An account whose prior review has gone stale, independent of whether it
// currently has any alert. Surfaced via its own "Re-review" badge.
export const showsNeedsRereview = (account) =>
  account.review_status === 'needs_rereview';

// Tooltip listing an account's suggested actions.
export const alertTitle = (account) =>
  (account.alerts || []).map((alert) => alert.label).join(', ') || undefined;

// UI-only suppression for recurring-flag cleanup suggestions. Keep the raw
// payload untouched so the signal can be reinstated later without backend
// changes.
export const isRecurringFlagAlert = (alert) => {
  const action = String(alert?.action || '').toLowerCase();
  const label = String(alert?.label || '').toLowerCase();
  const detail = String(alert?.detail || '').toLowerCase();
  return action.includes('recurring') || label.includes('recurring') || detail.includes('recurring flag');
};

// The id an account is grouped under: its Tamarac rebalancing group, or a
// synthetic per-account id when it belongs to no group (a singleton family).
export const familyIdOf = (account) => account.family_id || `acct:${account.account_number}`;

// The id an account gets when families are switched off. Deliberately NOT
// familyIdOf: two accounts of the same rebalancing group would share that id,
// collide on one React key, and expand together.
const soloIdOf = (account) => `acct:${account.account_number}`;

/**
 * Group reserve accounts into families, rolling up the aggregates the family
 * row displays and sorts on.
 *
 * The label is the backend's family_name — the Tamarac Rebalancing Group Name
 * (or the account's own name for singletons) — truncated for display, with the
 * full value in the tooltip.
 *
 * With `grouped: false` every account becomes its own single-account family, so
 * the ungrouped view renders through exactly the same row and detail components
 * and the two views cannot drift apart.
 */
export const buildFamilies = (accounts, { grouped = true } = {}) => {
  const idOf = grouped ? familyIdOf : soloIdOf;
  const byId = new Map();
  accounts.forEach((account) => {
    const id = idOf(account);
    if (!byId.has(id)) {
      byId.set(id, {
        family_id: id,
        family_full_name: grouped
          ? account.family_name || account.account_name || account.account_number
          : account.account_name || account.account_number,
        accounts: [],
      });
    }
    byId.get(id).accounts.push(account);
  });
  return Array.from(byId.values()).map((family) => {
    // Alert rollup: reviewed accounts are superseded (their alerts don't
    // bubble), matching the per-account display rules.
    const active = family.accounts.filter((a) => a.review_status !== 'reviewed');
    const alertCounts = active.reduce(
      (sum, a) => ({
        red: sum.red + (a.alert_counts?.red || 0),
        orange: sum.orange + (a.alert_counts?.orange || 0),
      }),
      { red: 0, orange: 0 }
    );
    const alertLevel = alertCounts.red > 0 ? 'red' : alertCounts.orange > 0 ? 'orange' : null;
    const alertLabels = [
      ...new Set(active.flatMap((a) => (a.alerts || []).map((alert) => alert.label))),
    ];
    return {
      ...family,
      family_name: truncateName(family.family_full_name),
      account_count: family.accounts.length,
      reserve_count: family.accounts.reduce((s, a) => s + (a.reserve_count || 0), 0),
      total_reserve_dollar: family.accounts.reduce((s, a) => s + (a.total_reserve_dollar || 0), 0),
      total_cash_dollar: family.accounts.reduce((s, a) => s + (a.total_cash_dollar || 0), 0),
      total_outflows: family.accounts.reduce((s, a) => s + (a.recent_outflows || 0), 0),
      // Summing account values is meaningful for a rebalancing group, but an
      // account with no reported value must not silently read as $0 — the count
      // lets the row footnote how many are missing.
      total_account_value: family.accounts.reduce(
        (s, a) => s + (a.account_current_value ?? 0),
        0
      ),
      accounts_missing_value: family.accounts.filter((a) => a.account_current_value == null)
        .length,
      needs_attention_count: family.accounts.filter(showsNeedsAttention).length,
      rereview_count: active.filter((a) => a.review_status === 'needs_rereview').length,
      alert_level: alertLevel,
      alert_counts: alertCounts,
      alert_labels: alertLabels,
      all_reviewed: family.accounts.every((a) => a.review_status === 'reviewed'),
      has_reviewed: family.accounts.some((a) => a.review_status === 'reviewed'),
      has_non_recurring: family.accounts.some(hasNonRecurringReserve),
      has_potential_duplicates: family.accounts.some((a) => a.has_potential_duplicates),
    };
  });
};

// ─── merging a family's per-account data ──────────────────────────────────────
/**
 * Flatten a family's alerts into one triage list.
 *
 * Ordered by level first, then by the account's position in the subtable, then
 * by the backend's own ordering. Deliberately NOT grouped fully by account —
 * that would bury the third account's red alerts under the first account's
 * orange ones, which is the opposite of triage.
 *
 * Each entry keeps its source account: three action codes (duplicate_reserve,
 * update_rmd_reserve, add_rmd_reserve) carry neither reserve_id nor
 * transaction_id and their detail text is never account-qualified, so the
 * account label is the only thing distinguishing two otherwise identical rows.
 * Applying a suggestion also needs it to target the right account.
 */
export const mergeAlerts = (accounts) => {
  const rank = (level) => (level === 'red' ? 0 : 1);
  return accounts
    .flatMap((account, ai) =>
      (account.alerts || []).map((alert, i) => ({
        alert,
        accountNumber: account.account_number,
        accountName: account.account_name,
        ai,
        i,
        // Identifies exactly one alert, so an Apply button's in-flight state
        // can never attach to a different one. The trailing index guarantees
        // uniqueness even if the backend ever emits two alerts with the same
        // action and ids; the cost is that resolving an earlier alert in the
        // same account shifts this key, remounting the button. That direction
        // is safe — it discards transient state rather than mixing it up.
        key: `${account.account_number}|${alert.action}|${alert.reserve_id ?? ''}|${
          alert.transaction_id ?? ''
        }|${i}`,
      }))
    )
    .sort((x, y) => rank(x.alert.level) - rank(y.alert.level) || x.ai - y.ai || x.i - y.i);
};

/** Every reserve in the family, tagged with the account that configured it. */
export const mergeReserves = (accounts) =>
  accounts.flatMap((account, ai) =>
    (account.reserves || []).map((reserve, i) => ({
      reserve,
      accountNumber: account.account_number,
      accountName: account.account_name,
      ai,
      i,
      // reserve_id is a fingerprint whose first field is the account number, so
      // it is unique family-wide; the fallback covers a missing id.
      key: reserve.reserve_id || `${account.account_number}|${i}`,
    }))
  );

/**
 * Merge per-account cash-flow summaries into one family-level summary.
 *
 * Counts and dollar figures are plain sums. Confidence is magnitude-weighted:
 * the backend defines it as classified_magnitude / total_magnitude, and since
 * its `outflow` accumulates negative impacts, `inflow - outflow` reproduces
 * that account's total magnitude exactly — so this is the same number, not an
 * approximation. Falls back to a count-weighted mean when there is no dollar
 * magnitude to weight by.
 */
export const mergeSummaries = (summaries) => {
  if (summaries.length === 0) return {};
  if (summaries.length === 1) return summaries[0];

  const merged = summaries.reduce(
    (acc, s) => {
      const weight = (s.inflow || 0) - (s.outflow || 0);
      return {
        transaction_count: acc.transaction_count + (s.transaction_count || 0),
        inflow: acc.inflow + (s.inflow || 0),
        outflow: acc.outflow + (s.outflow || 0),
        net_cash_impact: acc.net_cash_impact + (s.net_cash_impact || 0),
        weightedConfidence: acc.weightedConfidence + (s.confidence_score ?? 1) * weight,
        weight: acc.weight + weight,
        countConfidence:
          acc.countConfidence + (s.confidence_score ?? 1) * (s.transaction_count || 0),
        types: acc.types.concat(s.unclassified_activity_types || []),
      };
    },
    {
      transaction_count: 0,
      inflow: 0,
      outflow: 0,
      net_cash_impact: 0,
      weightedConfidence: 0,
      weight: 0,
      countConfidence: 0,
      types: [],
    }
  );

  let confidence;
  if (merged.weight > 0) {
    confidence = merged.weightedConfidence / merged.weight;
  } else if (merged.transaction_count > 0) {
    confidence = merged.countConfidence / merged.transaction_count;
  } else {
    confidence = 1;
  }

  return {
    transaction_count: merged.transaction_count,
    inflow: merged.inflow,
    outflow: merged.outflow,
    net_cash_impact: merged.net_cash_impact,
    confidence_score: confidence,
    unclassified_activity_types: [...new Set(merged.types)].sort(),
  };
};

// ─── column sorting ───────────────────────────────────────────────────────────
// Compare two cell values: numeric when both are numbers, otherwise a
// locale-aware, numeric-aware string comparison (so "10" sorts after "9").
export const compareValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

// Rank a status column for sorting: red alerts first, then re-review, then
// orange suggestions, quiet accounts, and reviewed last.
export const statusSortRank = (account) => {
  if (account.review_status === 'reviewed') return 4;
  if (account.alert_level === 'red') return 0;
  if (account.review_status === 'needs_rereview') return 1;
  if (account.alert_level === 'orange') return 2;
  return 3;
};

// Value accessors per sortable account column.
export const ACCOUNT_SORT_ACCESSORS = {
  account_name: (a) => a.account_name || '',
  account_number: (a) => a.account_number || '',
  account_type: (a) => a.account_type || '',
  status: statusSortRank,
  total_reserve_dollar: (a) => a.total_reserve_dollar || 0,
  total_cash_dollar: (a) => a.total_cash_dollar || 0,
  recent_outflows: (a) => a.recent_outflows || 0,
  account_current_value: (a) => a.account_current_value || 0,
  rmd_amount: (a) => a.rmd_amount || 0,
};

// Mirror the account status ranking at the family level.
export const familyStatusSortRank = (f) => {
  if (f.all_reviewed) return 4;
  if (f.alert_level === 'red') return 0;
  if (f.rereview_count > 0) return 1;
  if (f.alert_level === 'orange') return 2;
  return 3;
};

export const FAMILY_SORT_ACCESSORS = {
  family_full_name: (f) => f.family_full_name || '',
  account_count: (f) => f.account_count || 0,
  status: familyStatusSortRank,
  reserve_count: (f) => f.reserve_count || 0,
  total_reserve_dollar: (f) => f.total_reserve_dollar || 0,
  total_cash_dollar: (f) => f.total_cash_dollar || 0,
  total_outflows: (f) => f.total_outflows || 0,
  total_account_value: (f) => f.total_account_value || 0,
};

// Sort a copy of rows by the active column; returns null when no column is
// selected so callers can fall back to their default ordering.
export const sortByColumn = (rows, accessors, sortConfig) => {
  if (!sortConfig || !sortConfig.key) return null;
  const accessor = accessors[sortConfig.key];
  if (!accessor) return null;
  const dir = sortConfig.direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => dir * compareValues(accessor(a), accessor(b)));
};

// Tamarac's two reserve value types, spelled exactly as the report writes them.
// Most reserves are percent-typed (the 0.75% management fee), so a request form
// that only understood dollars would be wrong more often than right.
export const VALUE_TYPE_DOLLAR = 'Dollar';
export const VALUE_TYPE_PERCENT = 'PercentOfAccount';

export const isPercentValueType = (valueType) => {
  const text = String(valueType ?? '').trim().toLowerCase();
  // Mirrors reserve_scanner.is_percent_type on the backend.
  return text.includes('percent') || text === '%' || text === 'pct';
};

/**
 * A reserve amount as written, with its unit — "$24,000" or "0.75%".
 *
 * Distinct from the calculated dollar figure: a percent reserve's raw amount is
 * a rate, so rendering 0.75 as "$1" (which currency formatting does) is not
 * just ugly, it is wrong.
 */
export const fmtReserveAmount = (amount, valueType) => {
  if (amount == null || amount === '') return '—';
  if (!isPercentValueType(valueType)) return fmtCurrency(amount);
  const number = Number(amount);
  if (Number.isNaN(number)) return '—';
  // Trim trailing zeros: 0.75% not 0.7500%, but keep 0.755%.
  return `${parseFloat(number.toFixed(4))}%`;
};

// The tolerance band's percent spellings, which are NOT the amount's. A "33%"
// band means "33% OF the reserve" below and "33% ABOVE the reserve" above, so
// the two sides carry different type strings — and neither is PercentOfAccount.
// Mirrors reserve_scanner.py on the backend.
export const THRESHOLD_TYPE_LOWER_PERCENT = 'PercentOfReserve';
export const THRESHOLD_TYPE_UPPER_PERCENT = 'PercentAboveReserve';

/** The Tamarac type string for one side of a tolerance band. */
export const thresholdType = (isPercent, upper) => {
  if (!isPercent) return VALUE_TYPE_DOLLAR;
  return upper ? THRESHOLD_TYPE_UPPER_PERCENT : THRESHOLD_TYPE_LOWER_PERCENT;
};

/**
 * A tolerance band as one readable string.
 *
 * Renders "±33%" when both sides agree — which is 99% of real reserves — and
 * "−33% / +50%" when they don't, so the common case stays short without hiding
 * the uncommon one.
 */
export const fmtBand = (lower, lowerType, upper, upperType) => {
  const hasLower = lower != null && lower !== '';
  const hasUpper = upper != null && upper !== '';
  if (!hasLower && !hasUpper) return '';
  const low = hasLower ? fmtReserveAmount(lower, lowerType) : null;
  const high = hasUpper ? fmtReserveAmount(upper, upperType) : null;
  if (hasLower && hasUpper) {
    const symmetric =
      Number(lower) === Number(upper) &&
      isPercentValueType(lowerType) === isPercentValueType(upperType);
    return symmetric ? `±${low}` : `−${low} / +${high}`;
  }
  return hasLower ? `−${low}` : `+${high}`;
};

// Reserve-definition fields, in the wording the lineage diff shows.
export const DIFF_FIELD_LABELS = {
  reserve_category: 'category',
  reserve_value_type: 'value type',
  reserve_amount: 'amount',
  recurring: 'recurring flag',
  reserve_start_date: 'start date',
  reserve_end_date: 'end date',
  lower_threshold: 'lower threshold',
  lower_threshold_type: 'lower threshold type',
  upper_threshold: 'upper threshold',
  upper_threshold_type: 'upper threshold type',
  note: 'note',
};
